"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import {
  Activity,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  ThumbsUp,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";

import api from "../http";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";

type SeriesPoint = { period: string; value: number };
type FeedbackPoint = {
  period: string;
  total: number;
  positive: number;
  negative: number;
  neutral: number;
  positive_ratio: number;
};

type PerUser = {
  user_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  interactions: number;
  feedback_total: number;
  feedback_positive: number;
  feedback_negative: number;
  feedback_ratio: number;
  last_active: string | null;
};

type AnalyticsResponse = {
  range: { start: string; end: string; interval: string };
  summary: {
    interactions: number;
    active_users: number;
    feedback_total: number;
    feedback_positive: number;
    feedback_negative: number;
    feedback_neutral: number;
    positive_ratio: number;
    avg_interactions_per_user: number;
  };
  series: {
    active_users: SeriesPoint[];
    interactions: SeriesPoint[];
    feedback: FeedbackPoint[];
  };
  per_user: {
    data: PerUser[];
    total: number;
    limit: number;
    offset: number;
  };
};

const formatDateInput = (date: Date) => date.toISOString().split("T")[0];

const formatPeriodLabel = (period: string, interval: string) => {
  const parsed = new Date(period);
  if (Number.isNaN(parsed.getTime())) {
    return period;
  }

  if (interval === "month") {
    return parsed.toLocaleDateString("de-CH", { month: "short", year: "numeric" });
  }
  if (interval === "week") {
    const weekStart = new Date(parsed);
    const weekEnd = new Date(parsed);
    weekEnd.setDate(weekStart.getDate() + 6);
    return `${weekStart.toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
    })} - ${weekEnd.toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
    })}`;
  }

  return parsed.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "short",
  });
};

const Analytics = () => {
  const { getAccessToken } = useAuth();
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const [startDate, setStartDate] = useState<string>(() =>
    formatDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
  );
  const [endDate, setEndDate] = useState<string>(() => formatDateInput(new Date()));
  const [interval, setInterval] = useState<"day" | "week" | "month">("day");
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [perUserPage, setPerUserPage] = useState(1);
  const [perUserPageSize, setPerUserPageSize] = useState(10);
  const [perUserSortBy, setPerUserSortBy] = useState<
    "interactions" | "feedback_total" | "feedback_ratio" | "last_active"
  >("interactions");
  const [perUserSortDir, setPerUserSortDir] = useState<"asc" | "desc">("desc");

  const dateRangeValid = useMemo(
    () => new Date(startDate) <= new Date(endDate),
    [startDate, endDate],
  );

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (err) {
        console.error("Failed to load current user", err);
      }
    };

    loadCurrentUser();
  }, [getAccessToken]);

  const loadAnalytics = async () => {
    if (!dateRangeValid) {
      setError("Startdatum muss vor dem Enddatum liegen.");
      return;
    }

    if (!analytics) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const token = await getAccessToken();
      const data = await api.getAnalyticsOverview(token, {
        startDate,
        endDate,
        interval,
        perUserLimit: perUserPageSize,
        perUserOffset: (perUserPage - 1) * perUserPageSize,
        perUserSortBy,
        perUserSortDir,
      });
      setAnalytics(data);
    } catch (err) {
      console.error("Failed to load analytics", err);
      setError("Analytics konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, interval, perUserPage, perUserPageSize, perUserSortBy, perUserSortDir]);

  const usageData = useMemo(() => {
    if (!analytics) return [];
    const combined = new Map<string, { interactions: number; active: number }>();

    analytics.series.interactions.forEach((pt) => {
      combined.set(pt.period, {
        ...(combined.get(pt.period) ?? { interactions: 0, active: 0 }),
        interactions: pt.value,
      });
    });

    analytics.series.active_users.forEach((pt) => {
      combined.set(pt.period, {
        ...(combined.get(pt.period) ?? { interactions: 0, active: 0 }),
        active: pt.value,
      });
    });

    return Array.from(combined.entries())
      .map(([period, values]) => ({
        period,
        label: formatPeriodLabel(period, interval),
        interactions: values.interactions,
        active: values.active,
      }))
      .sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime(),
      );
  }, [analytics, interval]);

  const feedbackData = useMemo(
    () =>
      analytics?.series.feedback.map((pt) => ({
        period: pt.period,
        label: formatPeriodLabel(pt.period, interval),
        positive: pt.positive,
        negative: pt.negative,
        neutral: pt.neutral,
      })) ?? [],
    [analytics, interval],
  );

  const perUserData = analytics?.per_user?.data ?? [];
  const perUserTotal = analytics?.per_user?.total ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil((perUserTotal || 0) / perUserPageSize),
  );

  const summary = analytics?.summary;

  const renderSortArrows = (field: typeof perUserSortBy) => {
    const isAsc = perUserSortBy === field && perUserSortDir === "asc";
    const isDesc = perUserSortBy === field && perUserSortDir === "desc";
    return (
      <span className="flex flex-col ml-1">
        <button
          className="hover:bg-gray-100 rounded px-0.5 py-[1px]"
          onClick={() => {
            setPerUserSortBy(field);
            setPerUserSortDir("asc");
            setPerUserPage(1);
          }}
          aria-label={`${field} aufsteigend sortieren`}
        >
          <ChevronUp
            size={14}
            className={isAsc ? "" : "text-gray-400"}
            style={isAsc ? { color: APP_COLOR } : undefined}
          />
        </button>
        <button
          className="hover:bg-gray-100 rounded px-0.5 py-[1px]"
          onClick={() => {
            setPerUserSortBy(field);
            setPerUserSortDir("desc");
            setPerUserPage(1);
          }}
          aria-label={`${field} absteigend sortieren`}
        >
          <ChevronDown
            size={14}
            className={isDesc ? "" : "text-gray-400"}
            style={isDesc ? { color: APP_COLOR } : undefined}
          />
        </button>
      </span>
    );
  };

  const renderPageNumbers = () => (
    <div className="flex items-center gap-3">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <span
          key={page}
          onClick={() => setPerUserPage(page)}
          className={`cursor-pointer ${
            page === perUserPage
              ? "w-8 h-8 flex items-center justify-center rounded-full"
              : "text-gray-700 hover:text-gray-900"
          }`}
          style={
            page === perUserPage
              ? {
                  color: APP_COLOR,
                  backgroundColor: `${APP_COLOR}20`,
                }
              : undefined
          }
        >
          {page}
        </span>
      ))}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop header */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Analytics Dashboard
            </h1>
            <p className="text-sm text-slate-600">
              Nutzung, Interaktionen und Feedback auf einen Blick.
            </p>
          </div>
          <SettingsDropdown
            onOpenProfileModal={() => setIsProfileModalOpen(true)}
            currentUser={currentUser}
          />
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto pb-20 lg:pb-8">
          {/* Mobile header */}
          <div className="flex lg:hidden items-center justify-between mb-4">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                Analytics
              </h1>
              <p className="text-xs text-slate-600">Übersicht & KPIs</p>
            </div>
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>

          {/* Filters row */}
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2">
                <Calendar size={16} className="text-slate-500" />
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-slate-600" htmlFor="start-date">
                    Von
                  </label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setPerUserPage(1);
                      setStartDate(e.target.value);
                    }}
                    className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </div>
                <span className="text-slate-400">/</span>
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-slate-600" htmlFor="end-date">
                    Bis
                  </label>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setPerUserPage(1);
                      setEndDate(e.target.value);
                    }}
                    className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm">
                <span className="text-slate-600">Intervall</span>
                <select
                  value={interval}
                  onChange={(e) => {
                    setPerUserPage(1);
                    setInterval(e.target.value as "day" | "week" | "month");
                  }}
                  className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-slate-400 focus:outline-none"
                >
                  <option value="day">Täglich</option>
                  <option value="week">Wöchentlich</option>
                  <option value="month">Monatlich</option>
                </select>
              </div>
            </div>
          </div>

      {!dateRangeValid && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Startdatum muss vor dem Enddatum liegen.
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-4">
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktive Nutzer</CardTitle>
            <div
              className="rounded-full p-2 text-white"
              style={{ backgroundColor: APP_COLOR }}
            >
              <Activity size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {analytics ? summary?.active_users ?? "—" : "…"}
            </div>
            <p className="text-xs text-slate-500">
              Eindeutige Nutzer im Zeitraum
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Interaktionen</CardTitle>
            <div className="rounded-full bg-slate-100 p-2 text-slate-700">
              <MessageSquare size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {analytics ? summary?.interactions ?? "—" : "…"}
            </div>
            <p className="text-xs text-slate-500">
              Gestellte Fragen / Antworten
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Feedback</CardTitle>
            <div className="rounded-full bg-green-50 p-2 text-green-600">
              <ThumbsUp size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {analytics ? summary?.feedback_total ?? "—" : "…"}
            </div>
            <p className="text-xs text-slate-500">
              Abgegebene Bewertungen insgesamt
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positivquote</CardTitle>
            <div className="rounded-full bg-indigo-50 p-2 text-indigo-600">
              <BarChart3 size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {summary
                ? `${Math.round((summary.positive_ratio || 0) * 100)}%`
                : analytics
                ? "—"
                : "…"}
            </div>
            <p className="text-xs text-slate-500">
              Verhältnis Likes zu Gesamtfeedback
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Nutzung über Zeit</CardTitle>
            <CardDescription>
              Aktive Nutzer und Interaktionen pro Zeitraum.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!analytics && isLoading ? (
              <div className="py-12 text-center text-sm text-slate-500">
                Lade Diagramm…
              </div>
            ) : usageData.length ? (
              <ChartContainer
                className="h-[320px] w-full"
                config={{
                  interactions: { label: "Interaktionen", color: APP_COLOR },
                  active: { label: "Aktive Nutzer", color: "#94a3b8" },
                }}
              >
                <LineChart data={usageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend />
                  <Line
                    type="monotone"
                    dataKey="interactions"
                    stroke="var(--color-interactions)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="active"
                    stroke="var(--color-active)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ChartContainer>
            ) : (
              <div className="py-12 text-center text-sm text-slate-500">
                Noch keine Daten im ausgewählten Zeitraum.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Feedback-Entwicklung</CardTitle>
            <CardDescription>
              Positive vs. negative Bewertungen pro Zeitraum.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!analytics && isLoading ? (
              <div className="py-12 text-center text-sm text-slate-500">
                Lade Diagramm…
              </div>
            ) : feedbackData.length ? (
              <ChartContainer
                className="h-[320px] w-full"
                config={{
                  positive: { label: "Positiv", color: "#22c55e" },
                  negative: { label: "Negativ", color: "#ef4444" },
                }}
              >
                <BarChart data={feedbackData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend />
                  <Bar dataKey="positive" stackId="a" fill="var(--color-positive)" />
                  <Bar dataKey="negative" stackId="a" fill="var(--color-negative)" />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className="py-12 text-center text-sm text-slate-500">
                Keine Feedback-Daten im Zeitraum.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Interaktionen pro Nutzer</CardTitle>
            <CardDescription>
              Alle Nutzer mit Interaktionen im gewählten Zeitraum.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!analytics && isLoading ? (
              <div className="py-8 text-center text-sm text-slate-500">
                Lade Nutzerdaten…
              </div>
            ) : perUserData.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-3 pr-4 font-medium">Nutzer</th>
                      <th className="pb-3 pr-4 font-medium">
                        <div className="flex items-center gap-1">
                          Interaktionen
                          {renderSortArrows("interactions")}
                        </div>
                      </th>
                      <th className="pb-3 pr-4 font-medium">
                        <div className="flex items-center gap-1">
                          Feedback
                          {renderSortArrows("feedback_total")}
                        </div>
                      </th>
                      <th className="pb-3 pr-4 font-medium">
                        <div className="flex items-center gap-1">
                          Positivquote
                          {renderSortArrows("feedback_ratio")}
                        </div>
                      </th>
                      <th className="pb-3 font-medium">
                        <div className="flex items-center gap-1">
                          Zuletzt aktiv
                          {renderSortArrows("last_active")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {perUserData.map((user) => (
                      <tr
                        key={user.user_id}
                        className="border-t border-slate-100 text-slate-800"
                      >
                        <td className="py-3 pr-4">
                          <div className="font-semibold">
                            {user.first_name || user.last_name
                              ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                              : user.email}
                          </div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                        </td>
                        <td className="py-3 pr-4 font-semibold">
                          {user.interactions}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {user.feedback_total}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {`${Math.round((user.feedback_ratio || 0) * 100)}%`}
                        </td>
                        <td className="py-3 text-slate-600">
                          {user.last_active
                            ? new Date(user.last_active).toLocaleString("de-CH")
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">
                Keine Interaktionen in diesem Zeitraum.
              </div>
            )}
            {totalPages > 1 ? (
              <div className="mt-4 flex flex-col gap-3 text-sm text-slate-600 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setPerUserPage((p) => Math.max(1, p - 1))}
                    disabled={perUserPage === 1}
                    className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent disabled:cursor-not-allowed"
                  >
                    &lt; Zurück
                  </button>
                  {renderPageNumbers()}
                  <button
                    onClick={() =>
                      setPerUserPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={perUserPage >= totalPages}
                    className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent disabled:cursor-not-allowed"
                  >
                    Weiter &gt;
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <span>
                    Zeige {perUserData.length} von {perUserTotal} Nutzern
                  </span>
                  <select
                    value={perUserPageSize}
                    onChange={(e) => {
                      setPerUserPageSize(Number(e.target.value));
                      setPerUserPage(1);
                    }}
                    className="rounded-md border px-2 py-1"
                  >
                    {[10, 20, 50].map((size) => (
                      <option key={size} value={size}>
                        {size} / Seite
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="mt-4 text-sm text-slate-600">
                Alle Nutzer auf einer Seite ({perUserTotal || 0} Nutzer)
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>

    <ProfileImageModal
      isOpen={isProfileModalOpen}
      onClose={() => setIsProfileModalOpen(false)}
      currentUser={currentUser}
      onProfileImageUpdate={(imageUrl) => {
        if (currentUser) {
          setCurrentUser({ ...currentUser, profile_image_url: imageUrl });
        }
      }}
      onUserInfoUpdate={(updatedUser) => {
        setCurrentUser(updatedUser);
      }}
    />
  </div>
</div>
  );
};

export default Analytics;


